import type { Bulletin } from '@shared/types/Bulletin';

/**
 * Unicité d'un bulletin : UN seul bulletin utile par (salarié, période).
 * Un recalcul met à jour le bulletin existant ; il n'en crée jamais un second.
 * Ces fonctions servent aussi à lire sans erreur des données déjà dupliquées (versions antérieures),
 * sans jamais rien supprimer.
 */

/** Statuts après lesquels le bulletin ne bouge plus. */
const STATUTS_FIGES = new Set(['valide', 'paye', 'annule']);

/** Plus le rang est élevé, plus le bulletin est avancé : c'est celui que l'on retient. */
const RANG_STATUT: Record<string, number> = { genere: 1, valide: 2, paye: 3 };

function rang(b: Bulletin): number {
  return RANG_STATUT[b.statut ?? 'genere'] ?? 1;
}

function horodatage(b: Bulletin): string {
  return b.cree_le ?? b.date_emission ?? '';
}

/** Le bulletin à retenir entre deux bulletins du même salarié et de la même période. */
export function plusPertinent(a: Bulletin, b: Bulletin): Bulletin {
  const ra = rang(a);
  const rb = rang(b);
  if (ra !== rb) return ra > rb ? a : b;
  const ha = horodatage(a);
  const hb = horodatage(b);
  if (ha !== hb) return ha > hb ? a : b;
  return a.id >= b.id ? a : b;
}

function cle(b: Bulletin): string {
  return `${b.salarie_id}|${b.periode}`;
}

/** Un seul bulletin par (salarié, période), les bulletins annulés étant écartés. Ordre d'entrée conservé. */
export function retenirBulletinsUniques(bulletins: readonly Bulletin[]): Bulletin[] {
  const retenus = new Map<string, Bulletin>();
  for (const b of bulletins) {
    if (b.statut === 'annule') continue;
    const k = cle(b);
    const courant = retenus.get(k);
    retenus.set(k, courant ? plusPertinent(courant, b) : b);
  }
  return [...retenus.values()];
}

/** Groupes de bulletins non annulés partageant le même (salarié, période). Lecture seule : rien n'est supprimé. */
export function doublonsBulletins(bulletins: readonly Bulletin[]): Bulletin[][] {
  const groupes = new Map<string, Bulletin[]>();
  for (const b of bulletins) {
    if (b.statut === 'annule') continue;
    const k = cle(b);
    groupes.set(k, [...(groupes.get(k) ?? []), b]);
  }
  return [...groupes.values()].filter((g) => g.length > 1);
}

/** Tous les bulletins d'une période, regroupés par salarié (annulés inclus : ils comptent comme figés). */
export function bulletinsParSalarie(
  bulletins: readonly Bulletin[],
  periode: string,
): Map<string, Bulletin[]> {
  const parSalarie = new Map<string, Bulletin[]>();
  for (const b of bulletins) {
    if (b.periode !== periode) continue;
    parSalarie.set(b.salarie_id, [...(parSalarie.get(b.salarie_id) ?? []), b]);
  }
  return parSalarie;
}

export type DecisionRecalculBulletins =
  | { action: 'creer' }
  | { action: 'recalculer'; existants: Bulletin[] }
  | { action: 'ignorer'; motif: string };

/**
 * Décision de calcul pour un salarié et une période, avec les bulletins déjà présents (un ou plusieurs).
 * - aucun bulletin : création ;
 * - au moins un bulletin validé / payé / annulé : on ne touche à rien et on ne crée pas de nouveau bulletin ;
 * - sinon (tous « générés ») : recalcul, et TOUS les bulletins générés présents sont remplacés par un seul.
 */
export function decisionRecalculBulletins(
  existants: readonly Bulletin[],
): DecisionRecalculBulletins {
  if (existants.length === 0) return { action: 'creer' };
  const fige = existants.find((b) => STATUTS_FIGES.has(b.statut ?? 'genere'));
  if (fige) return { action: 'ignorer', motif: `bulletin déjà ${fige.statut}` };
  return { action: 'recalculer', existants: [...existants] };
}

/**
 * Création d'un bulletin unitaire : refusée s'il existe déjà un bulletin (hors annulé) pour ce salarié et cette période.
 * Pour corriger un bulletin « généré », on utilise « Calculer la paie » (recalcul), pas une nouvelle création.
 */
export function assertBulletinAbsent(
  existants: readonly Bulletin[],
  salarieId: string,
  periode: string,
): void {
  const deja = existants.find(
    (b) => b.salarie_id === salarieId && b.periode === periode && b.statut !== 'annule',
  );
  if (deja) {
    throw new Error(
      `Un bulletin existe déjà pour ce salarié sur ${periode} (statut : ${deja.statut ?? 'généré'}). ` +
        'Utilisez « Calculer la paie » pour le recalculer.',
    );
  }
}
